/**
 * The screen that sends a code to the account's own number so the person can
 * prove it. An account whose number was proven only by a claim link or a
 * roster gets `403 OTP_REQUIRED` from the actions that change what the account
 * is (connect a login provider or inbox, add an account, change the number,
 * delete); this is where those send them.
 */
export const STEP_UP_PATH = "/onboarding/confirm-number";

/** The step-up screen, returning to `returnTo` once the code is verified. */
export const stepUpHref = (returnTo: string) =>
  `${STEP_UP_PATH}?returnTo=${encodeURIComponent(returnTo)}`;

/**
 * A same-origin path to go back to, or null. One leading slash and no
 * backslash or whitespace: `//evil.example`, `/\\evil.example`, and absolute
 * URLs all normalize to another origin in some browser, and a crafted value
 * that reaches the History API throws, which would read to the person as a
 * good code being refused.
 */
export const safeReturnTo = (value: string | null) =>
  value?.startsWith("/") && !value.startsWith("//") && !value.includes("\\") && !/[\s]/.test(value)
    ? value
    : null;

/**
 * Whether a connect link starts one of the step-up-gated OAuth flows: a Google
 * or Microsoft account as a connector, an email inbox, or another account.
 * These navigate straight to the API, so the interceptor never sees the
 * refusal; the link itself has to point at the step-up instead.
 */
export const isStepUpGatedPath = (path: string) =>
  /\/api\/a\/(auth\/(google|microsoft)\/add-(connector|email-inbox|account)|connectors\/connect(-email-inbox)?\/(google|microsoft))(\?|$)/.test(
    path,
  ) ||
  // A re-auth that adds a capability widens access, like a connect; a plain
  // re-auth only renews and stays ungated.
  /\/api\/a\/auth\/(google|microsoft)\/reauth\/\d+\?(.*&)?add=/.test(path);
